<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="save">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="max-w-7xl mx-auto md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('Global AI prompts') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/settings/ai">
                            {{ $t('Back') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-12 gap-6">
                    <div class="xl:col-span-2">
                        <div class="bg-white shadow sm:rounded sticky top-4">
                            <div class="p-4">
                                <div class="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">{{ $t('Sections') }}</div>
                                <nav class="space-y-1">
                                    <router-link class="block px-3 py-2 text-sm text-gray-800 rounded bg-gray-100" to="/workspace/admin/settings/ai/global-prompts">
                                        {{ $t('Global AI prompts') }}
                                    </router-link>
                                    <router-link class="block px-3 py-2 text-sm text-gray-800 rounded hover:bg-gray-100" to="/workspace/admin/settings/ai/agents/ticket_summary">
                                        {{ $t('Agent (Ticket summary)') }}
                                    </router-link>
                                </nav>
                            </div>
                        </div>
                    </div>

                    <div class="xl:col-span-10 space-y-6">
                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User group prompts') }}</h3>
                                <p class="mt-2 text-sm text-gray-500 leading-5">
                                    {{ $t('Each grouped user scope has dedicated AI behavior rules (system prompt) and AI safety rules (safety prompt).') }}
                                </p>
                            </div>
                        </div>

                        <div
                            v-for="promptGroup in aiPromptGroups"
                            :key="'prompt-group-card-' + promptGroup.key"
                            class="bg-white shadow sm:rounded"
                        >
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ promptGroup.label }}</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ promptGroup.description }}
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                                    <div>
                                        <label class="stage-filter-label block mb-2" :for="'ai_system_prompt_group_' + promptGroup.key">{{ $t('AI behavior rules (system prompt)') }}</label>
                                        <div class="relative">
                                            <textarea
                                                :id="'ai_system_prompt_group_' + promptGroup.key"
                                                :value="getPromptGroupValue(promptGroup.key, 'system_prompt')"
                                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                                maxlength="10000"
                                                rows="6"
                                                @input="setPromptGroupValue(promptGroup.key, 'system_prompt', $event.target.value)"
                                            ></textarea>
                                        </div>
                                    </div>
                                    <div>
                                        <label class="stage-filter-label block mb-2" :for="'ai_safety_prompt_group_' + promptGroup.key">{{ $t('AI safety rules (safety prompt)') }}</label>
                                        <div class="relative">
                                            <textarea
                                                :id="'ai_safety_prompt_group_' + promptGroup.key"
                                                :value="getPromptGroupValue(promptGroup.key, 'safety_prompt')"
                                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                                maxlength="10000"
                                                rows="6"
                                                @input="setPromptGroupValue(promptGroup.key, 'safety_prompt', $event.target.value)"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: 'settings-ai-global-prompts',
    metaInfo() {
        return {
            title: this.$i18n.t('Global AI prompts')
        };
    },
    data() {
        return {
            loading: true,
            ai_prompt_groups_catalog: {},
            ai_enabled: false,
            ai_provider: 'openai',
            ai_default_locale: 'de',
            ai_openai_api_key: '',
            ai_openai_base_url: 'https://api.openai.com/v1',
            ai_timeout_seconds: 20,
            ai_retry_count: 1,
            ai_model_text: 'gpt-5-mini',
            ai_model_embedding: 'text-embedding-3-small',
            ai_pseudonymization_enabled: false,
            ai_prompts_by_group: {},
            ai_function_catalog: [],
            ai_functions_config: {},
            ai_monthly_budget: 50,
            ai_budget_currency: 'EUR',
            ai_budget_alert_percent: 80,
            ai_hard_stop_enabled: true,
            ai_max_tokens_per_request: 8000,
        };
    },
    mounted() {
        this.get().finally(() => {
            this.loading = false;
        });
    },
    computed: {
        aiPromptGroups() {
            return [
                {
                    key: 'employee',
                    label: this.$t('Employees'),
                    description: this.$t('Shared prompt rules for administrators and employees.'),
                },
                {
                    key: 'customer',
                    label: this.$t('Customer'),
                    description: this.$t('Prompt rules for customer-facing AI output.'),
                },
                {
                    key: 'guest',
                    label: this.$t('Guest'),
                    description: this.$t('Prompt rules for guest users with restricted context.'),
                },
            ];
        },
    },
    methods: {
        ensurePromptEntryForGroup(groupKey) {
            const normalizedKey = String(groupKey || '');
            if (!normalizedKey) {
                return;
            }
            if (!this.ai_prompts_by_group || typeof this.ai_prompts_by_group !== 'object') {
                this.ai_prompts_by_group = {};
            }
            if (!this.ai_prompts_by_group[normalizedKey]) {
                this.ai_prompts_by_group[normalizedKey] = {
                    system_prompt: '',
                    safety_prompt: '',
                };
            }
        },
        getPromptGroupValue(groupKey, field) {
            const normalizedKey = String(groupKey || '');
            const groupPrompts = this.ai_prompts_by_group && this.ai_prompts_by_group[normalizedKey]
                ? this.ai_prompts_by_group[normalizedKey]
                : null;
            return groupPrompts && groupPrompts[field] ? String(groupPrompts[field]) : '';
        },
        setPromptGroupValue(groupKey, field, value) {
            const normalizedKey = String(groupKey || '');
            this.ensurePromptEntryForGroup(normalizedKey);
            this.ai_prompts_by_group[normalizedKey][field] = value;
        },
        ensureAiFunctionConfigEntry(functionKey) {
            const selectedKey = functionKey ? String(functionKey) : null;
            if (!selectedKey) {
                return;
            }
            const meta = (this.ai_function_catalog || []).find((item) => String(item.key) === selectedKey);
            if (!meta) {
                return;
            }

            const defaults = meta.defaults && typeof meta.defaults === 'object'
                ? meta.defaults
                : {};
            const existing = this.ai_functions_config && typeof this.ai_functions_config[selectedKey] === 'object'
                ? this.ai_functions_config[selectedKey]
                : {};
            const normalized = Object.assign({}, defaults, existing);

            normalized.enabled = Boolean(normalized.enabled);
            normalized.prompt_template = normalized.prompt_template ? String(normalized.prompt_template) : null;
            normalized.cache_enabled = Boolean(
                Object.prototype.hasOwnProperty.call(normalized, 'cache_enabled') ? normalized.cache_enabled : true
            );
            if (normalized.cache_ttl_minutes === null || normalized.cache_ttl_minutes === '' || Number(normalized.cache_ttl_minutes) <= 0) {
                normalized.cache_ttl_minutes = null;
            } else {
                normalized.cache_ttl_minutes = Math.min(43200, Math.max(1, Number(normalized.cache_ttl_minutes || 10080)));
            }

            if (!this.ai_functions_config || typeof this.ai_functions_config !== 'object') {
                this.ai_functions_config = {};
            }
            this.ai_functions_config[selectedKey] = normalized;
        },
        get() {
            const self = this;
            return axios.get('api/workspace/admin/settings/ai').then(function (response) {
                self.ai_enabled = response.data.ai_enabled === null ? false : response.data.ai_enabled;
                self.ai_provider = response.data.ai_provider || 'openai';
                self.ai_default_locale = response.data.ai_default_locale || 'de';
                self.ai_openai_api_key = response.data.ai_openai_api_key || '';
                self.ai_openai_base_url = response.data.ai_openai_base_url || 'https://api.openai.com/v1';
                self.ai_timeout_seconds = response.data.ai_timeout_seconds === null ? 20 : Number(response.data.ai_timeout_seconds);
                self.ai_retry_count = response.data.ai_retry_count === null ? 1 : Number(response.data.ai_retry_count);
                self.ai_model_text = response.data.ai_model_text || 'gpt-5-mini';
                self.ai_model_embedding = response.data.ai_model_embedding || 'text-embedding-3-small';
                self.ai_pseudonymization_enabled = Boolean(response.data.ai_pseudonymization_enabled);
                self.ai_prompt_groups_catalog = response.data.ai_prompt_groups_catalog && typeof response.data.ai_prompt_groups_catalog === 'object'
                    ? response.data.ai_prompt_groups_catalog
                    : {};
                self.ai_prompts_by_group = response.data.ai_prompts_by_group && typeof response.data.ai_prompts_by_group === 'object'
                    ? response.data.ai_prompts_by_group
                    : {};
                self.ai_function_catalog = Array.isArray(response.data.ai_function_catalog)
                    ? response.data.ai_function_catalog
                    : [];
                self.ai_functions_config = response.data.ai_functions_config && typeof response.data.ai_functions_config === 'object'
                    ? response.data.ai_functions_config
                    : {};
                self.ai_monthly_budget = response.data.ai_monthly_budget === null ? 50 : Number(response.data.ai_monthly_budget);
                self.ai_budget_currency = response.data.ai_budget_currency || 'EUR';
                self.ai_budget_alert_percent = response.data.ai_budget_alert_percent === null ? 80 : Number(response.data.ai_budget_alert_percent);
                self.ai_hard_stop_enabled = response.data.ai_hard_stop_enabled === null ? true : response.data.ai_hard_stop_enabled;
                self.ai_max_tokens_per_request = response.data.ai_max_tokens_per_request === null ? 8000 : Number(response.data.ai_max_tokens_per_request);

                self.aiPromptGroups.forEach(function (group) {
                    self.ensurePromptEntryForGroup(group.key);
                });
                if (Array.isArray(self.ai_function_catalog)) {
                    self.ai_function_catalog.forEach(function (aiFunction) {
                        self.ensureAiFunctionConfigEntry(aiFunction.key);
                    });
                }
            }).catch(function () {
                self.$router.push('/workspace/admin/settings');
            });
        },
        save() {
            const self = this;
            self.loading = true;
            if (Array.isArray(self.ai_function_catalog)) {
                self.ai_function_catalog.forEach(function (aiFunction) {
                    self.ensureAiFunctionConfigEntry(aiFunction.key);
                });
            }
            axios.post('api/workspace/admin/settings/ai', {
                ai_enabled: self.ai_enabled ? 1 : 0,
                ai_provider: self.ai_provider,
                ai_default_locale: self.ai_default_locale,
                ai_openai_api_key: self.ai_openai_api_key || null,
                ai_openai_base_url: self.ai_openai_base_url || null,
                ai_timeout_seconds: self.ai_timeout_seconds,
                ai_retry_count: self.ai_retry_count,
                ai_model_text: self.ai_model_text,
                ai_model_embedding: self.ai_model_embedding || null,
                ai_pseudonymization_enabled: self.ai_pseudonymization_enabled ? 1 : 0,
                ai_prompts_by_group: self.ai_prompts_by_group,
                ai_functions_config: self.ai_functions_config,
                ai_monthly_budget: self.ai_monthly_budget,
                ai_budget_currency: self.ai_budget_currency,
                ai_budget_alert_percent: self.ai_budget_alert_percent,
                ai_hard_stop_enabled: self.ai_hard_stop_enabled ? 1 : 0,
                ai_max_tokens_per_request: self.ai_max_tokens_per_request,
            }).then(function () {
                return self.get();
            }).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        }
    }
}
</script>
